import { EmailLayout } from '~/App/Modules/Settings/MessageTemplates/Types';
import { UserWithSignatures as User } from '~/types/models';
import { GreetMetadata } from '~/types/redux/share';
export type { FromSignature } from './User';
import { StripeDiscount } from '../stripe';
import { ResourceTag } from './testimonial';
import { SocialType } from '~/App/Modules/Socials/util';
import Workspace from '~/App/Modules/Workspaces/Types/Workspace';
import { AddOn } from './addon';
import { FeatureDecisions } from '~/generated/FeatureDecisions';
import {
  MediaType,
  Reaction,
} from '@vimily/bonjoro-ui-components-v2/EmbedTile';
import { TranscriptSentence } from '@vimily/bonjoro-ui-components-v2/Transcript';
import { BonjoroService } from '../plans';
import { FallbackDelayUnit } from '~/App/Modules/Fallbacks/api';

export enum Service {
  VideoMessaging = 'video_messaging',
  Testimonials = 'testimonials',
}

export enum UserEmailPreviewType {
  Static = 'default',
  Gif = 'gif',
}

export enum UserRole {
  Admin = 'admin',
  Filmer = 'filmer',
}

export enum UserFlagType {
  SetupCompleted = 'setup_completed',
  GuideProgress = 'guide_progress',
  DisableFallbackConfirmation = 'disable_fallback_confirmation',
}

export interface UserFlags {
  [UserFlagType.SetupCompleted]?: boolean;
  [UserFlagType.GuideProgress]?: {
    [BonjoroService.VideoMessaging]?: Record<GuideProgressType, boolean>;
    [BonjoroService.Testimonials]?: Record<GuideProgressType, boolean>;
  };
  [UserFlagType.DisableFallbackConfirmation]?: boolean;
}

export interface UserSimple {
  id: string;
  email: string;
  is_email_verified: boolean;
  name: string;
  first_name: string | null;
  last_name: string | null;
  image: string;
  bio: string | null;
  lang: string;
  self_url: string;
  team_url: string;
  locale?: string;
  is_admin?: boolean;
  role?: UserRole;
  email_preview: UserEmailPreviewType;
  flags?: UserFlags;
}

export interface UserWithSignatures
  extends Pick<UserSimple, 'id' | 'email' | 'name'> {
  from_signature: Signature;
  replyto_signature: Signature;
  signatures?: Array<Signature> | null;
}

export interface TeamUser
  extends Pick<
    UserSimple,
    | 'id'
    | 'email'
    | 'name'
    | 'first_name'
    | 'last_name'
    | 'image'
    | 'bio'
    | 'lang'
  > {
  created_at: string;
  role: UserRole;
  services: Service[];
}

export interface TeamInvite
  extends Pick<
    UserSimple,
    'id' | 'email' | 'name' | 'first_name' | 'last_name'
  > {
  created_at: string;
  role: UserRole;
  services: Service[];
  team?: Team;
  creator?: TeamUser;
}

export interface ProductLimits {
  automations: number | null;
  message_templates: number | null;
  videos: number | null;
}

export interface Product {
  limits: ProductLimits;
}

export interface TeamPlan {
  id: string;
  nickname: string;
  interval: BillingCycle;
  product: Product;
}

export interface Team {
  id: string;
  name: string | null;
  landing_page_subdomain: string | null;
  landing_page_host: string;
  signatures?: Array<Signature>;
  industry_name?: string;
  reply_by_video: boolean;
  on_legacy_plan: boolean;
  billing_cycle: BillingCycle;
  total_users: number | null;
  vm_total_users: number | null;
  tm_total_users: number | null;
  payment_gateway: string;
}

export interface MessageTemplateTheme {
  id: string;
  primary_color: string;
  secondary_color: string;
  updated_at: string;
  created_at: string;
}
export interface MessageTemplateShare {
  id: string;
  primary_color: string;
  secondary_color: string;
  color: string;
  cta: MessageTemplateCallToActionShare;
  updated_at: string;
  created_at: string;
  embeds: EmbedItem[];
  template_type: MessageTemplateType;
  has_custom_logo: boolean;
  logo_url: string;
  background_url: string | null;
  is_greet_shareable: boolean;
}

export interface MessageTemplateMedia {
  id: string | null;
  name: string;
  tags: string[] | null;
  url: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface MessageTemplateCallToAction {
  id: string;
  text: string;
  uri: string;
  is_displayed?: boolean;
  updated_at: string;
  created_at: string;
}

export interface MessageTemplateCallToActionShare
  extends MessageTemplateCallToAction {
  url?: string;
  color: string;
  trueUrl: string;
}

export interface MessageTemplateLandingPage {
  background: MessageTemplateMedia | null;
  logo: MessageTemplateMedia | null;
  cta: MessageTemplateCallToAction;
}

export interface MessageTemplateStats {
  total: number;
  percentage_replies: number;
  percentage_views: number;
  percentage_reactions: number;
  percentage_clicks: number;
  percentage_opens: number;
}

export interface MessageTemplateEmailTemplate {
  layout: EmailLayout;
  subject_line: string;
  message_template: string;
}

export interface MessageTemplate {
  id: string;
  name: string | null;
  theme: MessageTemplateTheme;
  state: 'open' | 'demo' | 'locked';
  stats: MessageTemplateStats;
  email_template: MessageTemplateEmailTemplate;
  landing_page: MessageTemplateLandingPage;
  creator: UserSimple;
  created_at: string;
  updated_at: string;
  is_greet_shareable?: boolean;
  embeds: EmbedItem[];
  template_type: MessageTemplateType;
}

export interface ProfileEvent {
  id: string;
  message: string;
  occurred_at: string;
  type: string;
  source: string;
}

export enum GreetStatus {
  Open = 'open',
  Scheduled = 'scheduled',
  Closed = 'closed',
  Processing = 'processing',
  Blocked = 'blocked',
  Sent = 'sent',
  Error = 'error',
}

export enum GreetStatusReason {
  InvalidSenderSignature = 'invalid_rollup_sender_signature',
  UploadLimitReached = 'upload_limit_reached',
  None = '',
}

export type SocialLinks = {
  facebook?: string;
  linkedIn?: string;
  twitter?: string;
  other?: string;
};

export interface Contact {
  id: string;
  email: string;
  name: string | null;
  position: string | null;
  image: string;
  company_logo: string | null;
  profile_photo: string | null;
  social_links: SocialLinks;
}

export interface Profile extends Contact {
  first_name: string | null;
  last_name: string | null;
  work: {
    current: string | null;
    title: string | null;
  };
  demographic: {
    location: string | null;
    country: string | null;
    age: number | null;
    sex: string | null;
  };
  self_url: string;
  attributes_url: string;
  events_url: string;
  created_at: string;
  updated_at: string | null;
  sent_at: string | null;
  unsubscribed: boolean;
  summary: string | null;
  social_links: {
    platform: string;
    url: string;
  }[];
}

export interface Greet {
  id: string;
  assignee: UserSimple;
  event: ProfileEvent | null;
  status: GreetStatus;
  status_reason: GreetStatusReason;
  created_at: string;
  updated_at: string;
  last_modified_at: string;
  uploaded_at: string;
  send_at: string;
  thumb_url: string;
  record_url: string;
  uploads_url: string;
  public_url: string;
  profiles?: Profile[];
  profile: Profile;
  message_template: MessageTemplate;
  self_url: string;
  assignee_url: string;
  profiles_url: string;
  slug: string;
  metadata: GreetMetadata;
  greet_type: string;
  video_session?: VideoSession;
  campaign?: Workspace;
  links?: GreetLinks;
  transcript?: Transcript;
  automation_id?: number;
  send_type: GreetSendType;
}

export interface Transcript {
  id: string;
  status: 'queued' | 'complete' | 'error';
  captions?: {
    sentences?: TranscriptSentence[];
    audio_duration?: number;
    confidence?: number;
  };
}

export interface GreetLinks {
  putVideo: string;
  putThumbnail: string;
}

export type GreetDetails = Greet & {
  links: GreetLinks;
};

export enum GreetSendType {
  Manual = 'manual',
  Scheduled = 'scheduled',
  Fallback = 'fallback',
  /**
   * @deprecated
   */
  Optimised = 'optimised',

  /**
   * @deprecated
   */
  Default = 'default',
}

export enum GreetVideoSource {
  Recorded = 'recorded',
  ManualUpload = 'manual_upload',
}

export interface GreetShare {
  message: string;
}

export interface SignatureSimple {
  name: string;
  address: string;
  via: string;
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

export interface Signature extends SignatureSimple {
  id: string;
  address: string;
  /**
   * Sender signature is confirmed and is verified for sending.
   *
   * @see is_confirmed
   */
  is_verified: boolean;
  /**
   * Sender signature email address is confirmed. i.e. inbox is reachable.
   */
  is_confirmed: boolean;
  is_domain_verified: boolean;
  is_custom_transport: boolean;
  is_private: boolean;
  user_count: number;
  users: User[];
  transport_type: string;
  type: string;
  descriptive_address: string;
  forwarding_address: string;
  is_default: boolean;
}

export interface Domain {
  is_verified_as_sender: boolean | null;
  is_default: boolean | null;
  name: string;
  status: string | null;
  id: number;
  provider: string | null;
  provider_id: string | null;
  admin: {
    show_domain_url: string;
    open_provider_url: string;
  };
  is_compliant: boolean | null;
  requires_manual_verification: boolean | null;
  verify_domain_url: string | null;
  delete_domain_url: string | null;
  record_sets: RecordSet[];
  canonical?: Domain;
}

export interface Canonical {
  id: number;
  team_id: string;
  user_id: string | null;
  status: string;
  canonical_id: string;
  name: string;
  type: string;
  provider: string;
  provider_id: string | null;
  created_at: string;
  record_sets: RecordSet[];
}

export interface LandingDomain {
  id: number;
  team_id: string;
  user_id: string | null;
  status: string;
  canonical_id: string;
  name: string;
  type: string;
  provider: string;
  provider_id: string | null;
  created_at: string;
  canonical: Canonical;
  is_default?: boolean;
}

export interface RecordSet {
  type: string;
  host: string;
  key: string;
  value: string;
  is_verified: boolean;
}

export interface ResendRule {
  id: string;
  delay_days: number;
  subject: string;
  body: string;
  is_active: boolean;
}

export interface GreetResult extends Greet {
  uploaded_at: string;
}

export enum RecordingType {
  Webcam = 'webcam',
  ScreenRecording = 'screen_recording',
  Upload = 'upload',
  Testimonial = 'testimonial',
}

export enum RecordingState {
  Ready = 'ready',
  Error = 'error',
  Recording = 'recording',
}

export interface RecordingMessageTemplate {
  id: string;
  name: string;
  landing_page: MessageTemplateLandingPage;
  theme: MessageTemplateTheme;
  embeds: EmbedItem[];
  is_greeet_shareable: boolean;
  template_type: MessageTemplateType;
}

export interface Recording {
  uuid: string;
  id: string;
  name: string;
  message: string;
  upload_source: string;
  type: RecordingType;
  duration?: number;
  state: RecordingState;
  video_url: string;
  thumb_url: string;
  has_thumbnail: boolean;
  interactions?: {
    views?: number;
    clicks?: number;
  };
  media: {
    thumbnail_source: string;
    thumbnail_play: string;
  };
  public_url: string;
  iframe: string;
  created_at: string;
  message_template: RecordingMessageTemplate;
  author: Profile | null;
  transcript?: Transcript;
  tags?: ResourceTag[];
}

export interface Attribute {
  source: string;
  type: string | null;
  name: string;
  value: string | number | null;
}

export interface TransmissionEvent {
  transmission?: Transmission;
  event_type: string;
  message: string;
  created_at: string;
}

export interface Transmission {
  id: string;
  title: string;
  status: string;
  endpoint: string;
  created_at: string;
  events?: TransmissionEvent[];
  user?: UserSimple;
  signature?: Signature;
  recipient?: Profile;
}

export interface AutomationTriggerOrAction {
  id: string;
  name: string;
  short_name: string;
  service_id: string;
  logo_url: string;
}

export interface Automation {
  id: string;
  name: string | null;
  enabled: boolean;
  is_setup: boolean;
  trigger?: AutomationTriggerOrAction;
  action?: AutomationTriggerOrAction;
  fallback?: Fallback;
}

export enum FallbackVideoStatus {
  Created = 'created',
  Uploaded = 'uploaded',
  Encoding = 'encoding',
  Ready = 'ready',
  Error = 'error',
}

export interface FallbackVideo {
  id?: string;
  links?: {
    putThumbnail?: string;
    putVideo?: string;
  };
  video_url?: string;
  thumb_url?: string;
  status?: FallbackVideoStatus;
  message_template?: MessageTemplate;
  message?: string;
}

export enum FallbackFilterCondition {
  Match = 'match',
  Contains = 'contains',
}

export enum FallbackFilterInput {
  TaskDescription = 'task_description',
}
export interface FallbackFilterPredicate {
  condition: FallbackFilterCondition;
  input: FallbackFilterInput;
  value: string;
}

export type FallbackFilterGroup = FallbackFilterPredicate[];
export type FallbackFilters = FallbackFilterGroup[];
export type InputFallbackFilters = Partial<FallbackFilterPredicate>[][];

export interface Fallback {
  id: string;
  name: string;
  automation_id: string;
  delay_value?: number;
  delay_unit?: FallbackDelayUnit;
  ignore_weekends?: boolean;
  video?: FallbackVideo;
  filters?: FallbackFilters;
  scheduled_greet_count?: number;
  creator: UserSimple;
  created_at: string;
  updated_at: string;
}

export interface Timezone {
  value: string;
  label: string;
}

export interface Integration {
  id: string;
  is_setup: boolean;
  name: string;
  provider: string;
}

export enum MessageTemplateType {
  Postcard = 'postcard',
  NextGen = 'nextgen',
}

export interface OEmbed {
  type: string;
  version: string;
  provider_name?: string;
  provider_url?: string;
  html: string;
  icon?: string;
}

export enum EmbedType {
  OEmbed = 'oEmbed',
  Video = 'video_library',
  IFrame = 'iframe',
}

export interface EmbedItem {
  content_domain?: string;
  content_url?: string;
  model_id?: string;
  model?: Recording;
  data?: OEmbed;
  type: EmbedType;
  id?: number | string;
  recording?: Recording;
  icon: string;
  title: string;
}

export interface TrialInfo {
  days_left: number | null;
  days_left_text: string;
  within_two_weeks: boolean;
  period_days: number;
}

export enum PlanType {
  Starter = 'starter',
  Pro = 'pro',
  Grrrowth = 'grrrowth',
  Company = 'company',
}

export enum PlanUsageType {
  VideoUsage = 'Video usage',
  Templates = 'Video templates',
  Workflows = 'Workflows',
  Users = 'Users',
  VideoTestimonials = 'Video testimonials',
  Publishables = 'Publishables',
  FallbacksSent = 'Fallbacks sent',
}

export interface PlanUsage {
  title: PlanUsageType;
  count: number;
  subtitle?: string;
  limit?: number | null;
  chartColor?: string;
}

export enum BillingCycle {
  Monthly = 'month',
  Yearly = 'year',
}

export enum PaymentGateway {
  Stripe = 'stripe',
  Shopify = 'shopify',
}

export interface ProductPlan {
  id: string;
  name: string;
  image: string;
  description: string;
  discount: StripeDiscount | null;
  discount_formatted: string;
  discount_end: string;
  monthly_price_formatted: string;
  basic_price: string;
  original_price: number;
  billing_cycle: BillingCycle;
  features: FeatureDecisions;
  feature_descriptions: {
    active: string[];
    inactive: string[];
  };
  slug?: string;
  total: number;
  service: Service;
}

export interface CheckoutPlan extends ProductPlan {
  credit_card_saved: boolean;
  billing_description: string;
  total_bill: number;
  plan_price: number;
  on_trial: boolean;
  trial_ends_at: string;
  free_users_count: number;
  addons: Pick<AddOn, 'price' | 'quantity' | 'type'>[];
  additional_seat_price: number;
}

export interface AppSumoMetadata {
  max_codes: number;
}

export interface SubscriptionMetadata {
  app_sumo?: AppSumoMetadata;
}

export interface Subscription extends ProductPlan {
  trial_info: TrialInfo;
  usages: PlanUsage[];
  is_free: boolean;
  cancelled: boolean;
  billing_cycle: BillingCycle;
  cancelled_days_used: number | null;
  cancelled_days_left: number | null;
  cancelled_days_left_text: string;
  on_grace_period: true;
  video_message_limit: number | null;
  credit_card_saved: boolean;
  quantity: number | null;
  status: string | null;
  payment_gateway_id?: string;
  slug: string;
  service: Service;
  addons: AddOn[];
  metadata?: SubscriptionMetadata;
}

export interface CreditCard {
  id: string;
  payment_method_id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  name: string;
  is_default: boolean;
}

export interface Invoice {
  status: string;
  date: string;
  total: string;
  id: string;
  discount: string;
}

export interface InvoiceProfile {
  name: string | null;
  address: string | null;
  tax_id: string | null;
}

export interface UpcomingInvoice {
  date: string | null;
  total: string;
}

export interface InvoiceInformation {
  billing_profile: InvoiceProfile;
  upcoming_invoice: UpcomingInvoice | null;
  invoices: Invoice[];
}

export interface PaymentIntent {
  client_secret: string;
}

export interface Country {
  label: string;
  value: string;
}

export interface VideoSession {
  id: string;
  session_id: string;
  token: string;
  archive_id?: string;
  archive_status?: string;
  archive_url?: string;
}

export enum SubscriptionStatus {
  Active = 'active',
  Trialing = 'trialing',
}

export interface Media {
  id?: string | null;
  type?: string | null;
  name?: string | null;
  tags?: string | null;
  url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export enum SortOrder {
  Asc = 'asc',
  Desc = 'desc',
}

export enum IconType {
  FontAwesome = 'Font Awesome',
  Svg = 'Svg',
}
export interface SettingLinkList {
  title: string;
  iconClass: string;
  iconType?: IconType;
  links: SettingLink[];
}
export interface SettingLink {
  title: string;
  routeName: string;
  iconClass: string;
  event?: string;
  showNewBadge?: boolean;
  iconType?: IconType;
  id?: string;
}

export interface UserWebAsset {
  uuid: string;
  url: string;
  type: SocialType;
  content: UserWebAssetContent;
  created_at: string;
  author: Profile | null;
  tags?: ResourceTag[];
}

export interface UserWebAssetProfile {
  img_url?: string;
  name?: string;
  tag?: string;
}

export interface UserWebAssetMedia {
  type: MediaType;
  url?: string;
  preview_url?: string;
}

export interface UserWebAssetLink {
  url: string;
  img_url?: string;
  title?: string;
  description?: string;
}

export interface UserWebAssetContent {
  type: SocialType;
  id: string;
  profile?: UserWebAssetProfile;
  text?: string;
  media?: UserWebAssetMedia[];
  reactions?: Reaction[];
  links?: UserWebAssetLink[];
  datetime?: string;
  error?: {
    title: string;
  };
}

export enum NotificationType {
  User = 'user',
  Team = 'team',
}

export interface Notification {
  type: NotificationType;
  key: string;
  value: boolean;
  label: string;
  description?: string;
  note?: string;
  service?: Service;
}

export enum GuideProgressType {
  HasSentGreetToSelf = 'has-sent-greet-to-self',
  HasAddedMessageTemplate = 'has-added-message-template',
  HasSentGreetWithMessageTemplate = 'has-sent-greet-with-message-template',
  HasCustomEmailAddress = 'has-custom-email-address',
  HasSentGreetWithCustomEmailAddress = 'has-sent-greet-with-custom-email-address',
  HasAddedIntegration = 'has-added-integration',
  HasCompleteProfileDetails = 'has-complete-profile-details',

  HasCreatedProject = 'has-created-project',
  HasCopiedProjectLink = 'has-copied-project-link',
  HasUploadedTestimonialAsset = 'has-uploaded-testimonial-asset',
  HasVisitedTestimonialLibraryAllTab = 'has-visited-testimonial-library-all-tab',
  HasCreatedWallOfLove = 'has-created-wall-of-love',
  HasCreatedSlider = 'has-created-slider',
  HasCopiedPublishableLink = 'has-copied-publishable-link',
  HasUploadedSocialAsset = 'has-uploaded-social-asset',
}
